import axios, { AxiosError } from 'axios';
import {
    AlreadyAssignedErrorResponse,
    AlreadySubscribedErrorResponse,
    AppCheckErrorResponse,
    BeaconAlreadyExistsErrorResponse,
    BeaconNotFoundErrorResponse,
    CannotAssignOwnerRoleErrorResponse,
    CannotCreateTeamErrorResponse,
    CannotEnableArcWithoutContactsErrorResponse,
    CannotEnableOutOfHoursWithoutContactsErrorResponse,
    CannotInviteUsersWithOwnerErrorResponse,
    CannotInviteYourselfErrorResponse,
    CannotProcessFileErrorResponse,
    CannotRemoveContactsWithArcEnabledErrorResponse,
    CannotRemoveLastOwnerErrorResponse,
    CannotRemoveLastTeamErrorResponse,
    CannotRemoveOutOfHoursContactsWhenEnabledErrorResponse,
    CannotRemoveTeamErrorResponse,
    CannotRemoveTeamWithOpenTasksErrorResponse,
    CannotRemoveUserWithOpenTasksErrorResponse,
    CannotUpdateTeamErrorResponse,
    CompanyNotFoundErrorResponse,
    CompanyUserNotFoundErrorResponse,
    ConsentRequiredErrorResponse,
    DeviceAlreadyActivatedErrorResponse,
    DeviceNoPhoneErrorResponse,
    DeviceNoSmsPasswordErrorResponse,
    DeviceNotFoundErrorResponse,
    DeviceNotSetupErrorResponse,
    DuplicateUsersErrorResponse,
    EmailAlreadyInUseErrorResponse,
    EmailAlreadyVerifiedErrorResponse,
    EmailBlockedErrorResponse,
    ForbiddenErrorResponse,
    ForbiddenUnauthorisedErrorResponse,
    IncorrectCodeErrorResponse,
    IncorrectPasswordErrorResponse,
    IncorrectTokenErrorResponse,
    InvalidCredentialsErrorResponse,
    InvalidSsoTokenErrorResponse,
    InvalidTeamErrorResponse,
    InvalidTokenErrorResponse,
    InvalidUserErrorResponse,
    InviteNotFoundErrorResponse,
    IpBlockedErrorResponse,
    NoMoreLicensesErrorResponse,
    NoPermissionErrorResponse,
    NoSmsPaymentMethodErrorResponse,
    NoSmsSubscriptionErrorResponse,
    NotSubscribedErrorResponse,
    SimClosedErrorResponse,
    SmsAlreadyDisabledErrorResponse,
    SmsAlreadyEnabledErrorResponse,
    SsoAccountTypeNotSupportedErrorResponse,
    SsoEmailNotVerifiedErrorResponse,
    SsoIdentityAlreadyLinkedErrorResponse,
    SsoProviderNotConfiguredErrorResponse,
    TaskNotAlertedErrorResponse,
    TaskNotFoundErrorResponse,
    TeamNotFoundErrorResponse,
    TooManyCodeAttemptsErrorResponse,
    TooManyRequestsErrorResponse,
    UnauthorisedErrorResponse,
    UserAlreadyExistsErrorResponse,
    UserAlreadyInTeamErrorResponse,
    UserAlreadyInvitedOrJoinedErrorResponse,
    UserAlreadyJoinedErrorResponse,
    UserNotInTeamErrorResponse,
    UserNotFoundErrorResponse,
    ValidationErrorResponse
} from '@/api';

type ErrorByCode = {
    INVALID_CREDENTIALS: InvalidCredentialsErrorResponse;
    TOO_MANY_REQUESTS: TooManyRequestsErrorResponse;
    USER_ALREADY_EXISTS: UserAlreadyExistsErrorResponse;
    APP_CHECK_FAILED: AppCheckErrorResponse;
    IP_BLOCKED: IpBlockedErrorResponse;
    EMAIL_BLOCKED: EmailBlockedErrorResponse;
    EMAIL_ALREADY_IN_USE: EmailAlreadyInUseErrorResponse;
    EMAIL_ALREADY_VERIFIED: EmailAlreadyVerifiedErrorResponse;
    INCORRECT_PASSWORD: IncorrectPasswordErrorResponse;
    BAD_REQUEST: ValidationErrorResponse;
    UNAUTHORISED: UnauthorisedErrorResponse;
    /**
     * The 403 refusal, which the API also labels `UNAUTHORISED`. Distinct from
     * `UNAUTHORISED` above, which is the 401 — an expired session rather than
     * a permission the caller does not have. Nearly every write endpoint
     * declares this one.
     */
    FORBIDDEN: ForbiddenUnauthorisedErrorResponse;
    /**
     * The other 403, which says only `Forbidden` — the guard the beacon
     * endpoints declare rather than the `UNAUTHORISED` one everything else
     * uses. Same meaning to a user, different wire format.
     */
    FORBIDDEN_RESOURCE: ForbiddenErrorResponse;
    USER_NOT_FOUND: UserNotFoundErrorResponse;
    TEAM_NOT_FOUND: TeamNotFoundErrorResponse;
    INVALID_TOKEN: InvalidTokenErrorResponse;
    INCORRECT_TOKEN: IncorrectTokenErrorResponse;
    INCORRECT_CODE: IncorrectCodeErrorResponse;
    TOO_MANY_CODE_ATTEMPTS: TooManyCodeAttemptsErrorResponse;
    DEVICE_NOT_FOUND: DeviceNotFoundErrorResponse;
    DEVICE_NOT_SETUP: DeviceNotSetupErrorResponse;
    DEVICE_NO_PHONE: DeviceNoPhoneErrorResponse;
    DEVICE_NO_SMS_PASSWORD: DeviceNoSmsPasswordErrorResponse;
    DEVICE_ALREADY_ACTIVATED: DeviceAlreadyActivatedErrorResponse;
    SIM_CLOSED: SimClosedErrorResponse;
    NOT_SUBSCRIBED: NotSubscribedErrorResponse;
    NO_SMS_SUBSCRIPTION: NoSmsSubscriptionErrorResponse;
    NO_MORE_LICENSES: NoMoreLicensesErrorResponse;
    COMPANY_NOT_FOUND: CompanyNotFoundErrorResponse;
    COMPANY_USER_NOT_FOUND: CompanyUserNotFoundErrorResponse;
    INVALID_TEAM: InvalidTeamErrorResponse;
    INVALID_USER: InvalidUserErrorResponse;
    BEACON_ALREADY_EXISTS: BeaconAlreadyExistsErrorResponse;
    BEACON_NOT_FOUND: BeaconNotFoundErrorResponse;
    CANNOT_REMOVE_USER_WITH_OPEN_TASKS: CannotRemoveUserWithOpenTasksErrorResponse;
    CANNOT_REMOVE_LAST_OWNER: CannotRemoveLastOwnerErrorResponse;
    CANNOT_ASSIGN_OWNER_ROLE: CannotAssignOwnerRoleErrorResponse;
    USER_ALREADY_JOINED: UserAlreadyJoinedErrorResponse;
    USER_NOT_IN_TEAM: UserNotInTeamErrorResponse;
    USER_ALREADY_IN_TEAM: UserAlreadyInTeamErrorResponse;
    USER_ALREADY_INVITED_OR_JOINED: UserAlreadyInvitedOrJoinedErrorResponse;
    CANNOT_INVITE_YOURSELF: CannotInviteYourselfErrorResponse;
    CANNOT_INVITE_USERS_WITH_OWNER: CannotInviteUsersWithOwnerErrorResponse;
    DUPLICATE_USERS: DuplicateUsersErrorResponse;
    NO_PERMISSION: NoPermissionErrorResponse;
    INVITE_NOT_FOUND: InviteNotFoundErrorResponse;
    CANNOT_CREATE_TEAM: CannotCreateTeamErrorResponse;
    CANNOT_UPDATE_TEAM: CannotUpdateTeamErrorResponse;
    CANNOT_REMOVE_TEAM: CannotRemoveTeamErrorResponse;
    CANNOT_REMOVE_LAST_TEAM: CannotRemoveLastTeamErrorResponse;
    CANNOT_REMOVE_TEAM_WITH_OPEN_TASKS: CannotRemoveTeamWithOpenTasksErrorResponse;
    CANNOT_ENABLE_ARC_WITHOUT_CONTACTS: CannotEnableArcWithoutContactsErrorResponse;
    CANNOT_REMOVE_CONTACTS_WITH_ARC_ENABLED: CannotRemoveContactsWithArcEnabledErrorResponse;
    CANNOT_ENABLE_OUT_OF_HOURS_WITHOUT_CONTACTS: CannotEnableOutOfHoursWithoutContactsErrorResponse;
    CANNOT_REMOVE_OUT_OF_HOURS_CONTACTS_WHEN_ENABLED: CannotRemoveOutOfHoursContactsWhenEnabledErrorResponse;
    ALREADY_SUBSCRIBED: AlreadySubscribedErrorResponse;
    SMS_ALREADY_ENABLED: SmsAlreadyEnabledErrorResponse;
    SMS_ALREADY_DISABLED: SmsAlreadyDisabledErrorResponse;
    NO_SMS_PAYMENT_METHOD: NoSmsPaymentMethodErrorResponse;
    CANNOT_PROCESS_FILE: CannotProcessFileErrorResponse;
    CONSENT_REQUIRED: ConsentRequiredErrorResponse;
    SSO_PROVIDER_NOT_CONFIGURED: SsoProviderNotConfiguredErrorResponse;
    INVALID_SSO_TOKEN: InvalidSsoTokenErrorResponse;
    SSO_EMAIL_NOT_VERIFIED: SsoEmailNotVerifiedErrorResponse;
    SSO_ACCOUNT_TYPE_NOT_SUPPORTED: SsoAccountTypeNotSupportedErrorResponse;
    SSO_IDENTITY_ALREADY_LINKED: SsoIdentityAlreadyLinkedErrorResponse;
    TASK_NOT_FOUND: TaskNotFoundErrorResponse;
    /**
     * The task stopped being an alert before the button was pressed — someone
     * else resolved it, or the worker checked in.
     */
    TASK_NOT_ALERTED: TaskNotAlertedErrorResponse;
    ALREADY_ASSIGNED: AlreadyAssignedErrorResponse;
};

export type ApiErrorCode = keyof ErrorByCode;

type ErrorSpec = {
    statusCode: number;
    match?: (data: Record<string, unknown>) => boolean;
};

const registry: { [K in ApiErrorCode]: ErrorSpec } = {
    INVALID_CREDENTIALS: {
        statusCode: 401,
        match: (d) => d?.error === 'INVALID_CREDENTIALS'
    },
    TOO_MANY_REQUESTS: { statusCode: 429 },
    USER_ALREADY_EXISTS: {
        statusCode: 400,
        match: (d) => d?.message === 'USER_ALREADY_EXISTS'
    },
    APP_CHECK_FAILED: {
        statusCode: 403,
        match: (d) => d?.message === 'APP_CHECK_FAILED'
    },
    IP_BLOCKED: { statusCode: 403, match: (d) => d?.message === 'IP_BLOCKED' },
    EMAIL_BLOCKED: {
        statusCode: 400,
        match: (d) => d?.message === 'EMAIL_BLOCKED'
    },
    EMAIL_ALREADY_IN_USE: {
        statusCode: 400,
        match: (d) => d?.message === 'EMAIL_ALREADY_IN_USE'
    },
    EMAIL_ALREADY_VERIFIED: {
        statusCode: 400,
        match: (d) => d?.message === 'EMAIL_ALREADY_VERIFIED'
    },
    INCORRECT_PASSWORD: {
        statusCode: 400,
        match: (d) => d?.message === 'INCORRECT_PASSWORD'
    },
    BAD_REQUEST: {
        statusCode: 400,
        match: (d) => d?.error === 'Bad Request' && Array.isArray(d?.message)
    },
    UNAUTHORISED: {
        statusCode: 401,
        match: (d) => d?.message === 'UNAUTHORISED'
    },
    // Same `message` as UNAUTHORISED, different status. Matching on the status
    // is the only thing that separates "your session expired" from "you are
    // not allowed to do that".
    FORBIDDEN: {
        statusCode: 403,
        match: (d) => d?.message === 'UNAUTHORISED'
    },
    FORBIDDEN_RESOURCE: {
        statusCode: 403,
        match: (d) => d?.message === 'Forbidden'
    },
    USER_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'USER_NOT_FOUND'
    },
    TEAM_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'TEAM_NOT_FOUND'
    },
    INVALID_TOKEN: {
        statusCode: 400,
        match: (d) => d?.message === 'INVALID_TOKEN'
    },
    INCORRECT_TOKEN: {
        statusCode: 400,
        match: (d) => d?.message === 'INCORRECT_TOKEN'
    },
    INCORRECT_CODE: {
        statusCode: 400,
        match: (d) => d?.message === 'INCORRECT_CODE'
    },
    TOO_MANY_CODE_ATTEMPTS: {
        statusCode: 400,
        match: (d) => d?.message === 'TOO_MANY_CODE_ATTEMPTS'
    },
    DEVICE_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'DEVICE_NOT_FOUND'
    },
    DEVICE_NOT_SETUP: {
        statusCode: 400,
        match: (d) => d?.message === 'DEVICE_NOT_SETUP'
    },
    DEVICE_NO_PHONE: {
        statusCode: 400,
        match: (d) => d?.message === 'DEVICE_NO_PHONE'
    },
    DEVICE_NO_SMS_PASSWORD: {
        statusCode: 400,
        match: (d) => d?.message === 'DEVICE_NO_SMS_PASSWORD'
    },
    DEVICE_ALREADY_ACTIVATED: {
        statusCode: 409,
        match: (d) => d?.message === 'DEVICE_ALREADY_ACTIVATED'
    },
    SIM_CLOSED: {
        statusCode: 409,
        match: (d) => d?.message === 'SIM_CLOSED'
    },
    NOT_SUBSCRIBED: {
        statusCode: 403,
        match: (d) => d?.message === 'NOT_SUBSCRIBED'
    },
    NO_SMS_SUBSCRIPTION: {
        statusCode: 403,
        match: (d) => d?.message === 'NO_SMS_SUBSCRIPTION'
    },
    NO_MORE_LICENSES: {
        statusCode: 403,
        match: (d) => d?.message === 'NO_MORE_LICENSES'
    },
    COMPANY_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'COMPANY_NOT_FOUND'
    },
    COMPANY_USER_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'COMPANY_USER_NOT_FOUND'
    },
    INVALID_TEAM: {
        statusCode: 400,
        match: (d) => d?.message === 'INVALID_TEAM'
    },
    INVALID_USER: {
        statusCode: 400,
        match: (d) => d?.message === 'INVALID_USER'
    },
    BEACON_ALREADY_EXISTS: {
        statusCode: 400,
        match: (d) => d?.message === 'BEACON_ALREADY_EXISTS'
    },
    BEACON_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'BEACON_NOT_FOUND'
    },
    CANNOT_REMOVE_USER_WITH_OPEN_TASKS: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_REMOVE_USER_WITH_OPEN_TASKS'
    },
    CANNOT_REMOVE_LAST_OWNER: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_REMOVE_LAST_OWNER'
    },
    CANNOT_ASSIGN_OWNER_ROLE: {
        statusCode: 403,
        match: (d) => d?.message === 'CANNOT_ASSIGN_OWNER_ROLE'
    },
    USER_ALREADY_JOINED: {
        statusCode: 403,
        match: (d) => d?.message === 'USER_ALREADY_JOINED'
    },
    USER_NOT_IN_TEAM: {
        statusCode: 400,
        match: (d) => d?.message === 'USER_NOT_IN_TEAM'
    },
    USER_ALREADY_IN_TEAM: {
        statusCode: 400,
        match: (d) => d?.message === 'USER_ALREADY_IN_TEAM'
    },
    USER_ALREADY_INVITED_OR_JOINED: {
        statusCode: 403,
        match: (d) => d?.message === 'USER_ALREADY_INVITED_OR_JOINED'
    },
    CANNOT_INVITE_YOURSELF: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_INVITE_YOURSELF'
    },
    CANNOT_INVITE_USERS_WITH_OWNER: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_INVITE_USERS_WITH_OWNER'
    },
    DUPLICATE_USERS: {
        statusCode: 400,
        match: (d) => d?.message === 'DUPLICATE_USERS'
    },
    NO_PERMISSION: {
        statusCode: 403,
        match: (d) => d?.message === 'NO_PERMISSION'
    },
    INVITE_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'INVITE_NOT_FOUND'
    },
    CANNOT_CREATE_TEAM: {
        statusCode: 403,
        match: (d) => d?.message === 'CANNOT_CREATE_TEAM'
    },
    CANNOT_UPDATE_TEAM: {
        statusCode: 403,
        match: (d) => d?.message === 'CANNOT_UPDATE_TEAM'
    },
    CANNOT_REMOVE_TEAM: {
        statusCode: 403,
        match: (d) => d?.message === 'CANNOT_REMOVE_TEAM'
    },
    CANNOT_REMOVE_LAST_TEAM: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_REMOVE_LAST_TEAM'
    },
    CANNOT_REMOVE_TEAM_WITH_OPEN_TASKS: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_REMOVE_TEAM_WITH_OPEN_TASKS'
    },
    CANNOT_ENABLE_ARC_WITHOUT_CONTACTS: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_ENABLE_ARC_WITHOUT_CONTACTS'
    },
    CANNOT_REMOVE_CONTACTS_WITH_ARC_ENABLED: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_REMOVE_CONTACTS_WITH_ARC_ENABLED'
    },
    CANNOT_ENABLE_OUT_OF_HOURS_WITHOUT_CONTACTS: {
        statusCode: 400,
        match: (d) =>
            d?.message === 'CANNOT_ENABLE_OUT_OF_HOURS_WITHOUT_CONTACTS'
    },
    CANNOT_REMOVE_OUT_OF_HOURS_CONTACTS_WHEN_ENABLED: {
        statusCode: 400,
        match: (d) =>
            d?.message === 'CANNOT_REMOVE_OUT_OF_HOURS_CONTACTS_WHEN_ENABLED'
    },
    ALREADY_SUBSCRIBED: {
        statusCode: 403,
        match: (d) => d?.message === 'ALREADY_SUBSCRIBED'
    },
    SMS_ALREADY_ENABLED: {
        statusCode: 403,
        match: (d) => d?.message === 'SMS_ALREADY_ENABLED'
    },
    SMS_ALREADY_DISABLED: {
        statusCode: 403,
        match: (d) => d?.message === 'SMS_ALREADY_DISABLED'
    },
    NO_SMS_PAYMENT_METHOD: {
        statusCode: 400,
        match: (d) => d?.message === 'NO_SMS_PAYMENT_METHOD'
    },
    CANNOT_PROCESS_FILE: {
        statusCode: 400,
        match: (d) => d?.message === 'CANNOT_PROCESS_FILE'
    },
    CONSENT_REQUIRED: {
        statusCode: 400,
        match: (d) => d?.message === 'CONSENT_REQUIRED'
    },
    SSO_PROVIDER_NOT_CONFIGURED: {
        statusCode: 400,
        match: (d) => d?.message === 'SSO_PROVIDER_NOT_CONFIGURED'
    },
    INVALID_SSO_TOKEN: {
        statusCode: 401,
        match: (d) => d?.message === 'INVALID_SSO_TOKEN'
    },
    SSO_EMAIL_NOT_VERIFIED: {
        statusCode: 403,
        match: (d) => d?.message === 'SSO_EMAIL_NOT_VERIFIED'
    },
    SSO_ACCOUNT_TYPE_NOT_SUPPORTED: {
        statusCode: 403,
        match: (d) => d?.message === 'SSO_ACCOUNT_TYPE_NOT_SUPPORTED'
    },
    SSO_IDENTITY_ALREADY_LINKED: {
        statusCode: 409,
        match: (d) => d?.message === 'SSO_IDENTITY_ALREADY_LINKED'
    },
    TASK_NOT_FOUND: {
        statusCode: 404,
        match: (d) => d?.message === 'TASK_NOT_FOUND'
    },
    TASK_NOT_ALERTED: {
        statusCode: 403,
        match: (d) => d?.message === 'TASK_NOT_ALERTED'
    },
    ALREADY_ASSIGNED: {
        statusCode: 403,
        match: (d) => d?.message === 'ALREADY_ASSIGNED'
    }
};

export function isApiError<K extends ApiErrorCode>(
    error: unknown,
    code: K
): error is AxiosError<ErrorByCode[K]> {
    if (!axios.isAxiosError(error)) return false;
    const spec = registry[code];
    if (error.response?.status !== spec.statusCode) return false;
    const data = error.response?.data as Record<string, unknown> | undefined;
    if (!data) return false;
    return spec.match ? spec.match(data) : true;
}

/**
 * Seconds until the rate-limit window resets, or null if the error is not a
 * rate-limit error or the server did not say.
 */
export function getRetryAfterSeconds(error: unknown): number | null {
    if (!isApiError(error, 'TOO_MANY_REQUESTS')) return null;

    const retryAfter = error.response?.data?.retryAfter;
    return typeof retryAfter === 'number' ? retryAfter : null;
}

/**
 * How an image upload says it rejected the file.
 *
 * These are not in `openapi.json` — the upload endpoints declare only
 * `CANNOT_PROCESS_FILE` — so `isApiError` has nothing to match on. They are
 * worth catching anyway: "that file is too big" is the one upload failure a
 * user can actually do something about.
 */
export type ImageRejectionCode =
    | 'UNSUPPORTED_FILE_FORMAT'
    | 'UNSUPPORTED_FILE_SIZE'
    | 'UNSUPPORTED_IMAGE_DIMENSIONS';

const IMAGE_REJECTION_CODES: ImageRejectionCode[] = [
    'UNSUPPORTED_FILE_FORMAT',
    'UNSUPPORTED_FILE_SIZE',
    'UNSUPPORTED_IMAGE_DIMENSIONS'
];

export function getImageRejectionCode(
    error: unknown
): ImageRejectionCode | null {
    if (!axios.isAxiosError(error)) return null;

    const data = error.response?.data as Record<string, unknown> | undefined;
    const message = data?.message;

    return IMAGE_REJECTION_CODES.find((code) => code === message) ?? null;
}

export type FallbackErrorKey =
    | 'shared.errors.generic'
    | 'shared.errors.unable_to_reach_server';

/**
 * The i18n key for an error that no `isApiError` check has claimed. Use it as
 * the last branch once the specific errors have been handled.
 */
export function getFallbackErrorKey(error: unknown): FallbackErrorKey {
    // A request with no response never got an answer: offline, DNS failure or
    // a timeout. Anything else (a 500, an unhandled 4xx) did reach the server.
    if (axios.isAxiosError(error) && !error.response && error.request) {
        return 'shared.errors.unable_to_reach_server';
    }

    return 'shared.errors.generic';
}
